import Grid from '@material-ui/core/Grid';
import ResourceIcon from '../core/icons/Resource';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { APPLICATION_PATHS, ENTITLEMENTS } from '../application-core/constants';
import ComponentTitle from '../core/ComponentTitle';
import InAppBar from '../core/InAppBar';
import Warning from '../core/Warning';
import { STATUS_DRAFT } from '../core/utils/constants';
import { checkResourceEntitlements, processHashLocation } from '../core/utils/utils';
import withGS from '../core/withGShoc';
import GS from '../grafsync/grafsync';
import ResourceView from './ResourceView';

let tabItems = [
  {
    icon: ResourceIcon,
    label: "Resources"
  }];

class DatasetEditorContainer extends Component {

  constructor(props) {
    super(props);
    this.state = {
      tabValue: 0,
      viewLang: props.defaultLang,
      deletedResources: [],
      activeResource: null,
      isErrored: false
    };
    this.cancellable = null;
  }

  static getDerivedStateFromProps(props, state) {
    const paramsMap = processHashLocation(props.location);
    let resource = paramsMap.get("resource");
    const escape = paramsMap.get("escape");
    if (escape === "true") resource = resource.replace("%23", '#');
    // But we only need to override the local state if locally we're not aligned with the updated prop
    if (resource !== state.activeResource) {
      return {
        ...(state.activeResource !== resource) &&
        { activeResource: (resource === undefined || resource === null || resource === "") ? null : resource }
      };
    }
    return null;
  }

  buildLink(resource) {
    let link = APPLICATION_PATHS.DATA_MANAGER + '/project/dataset#project='
      + this.props.project + "&dataset=" + this.props.id;
    link += resource !== null ? "&resource=" + resource : "";
    return link;
  }

  toggleDetail = () => this.props.toggleDetail(0);

  componentWillUnmount() {
    clearTimeout(this.langTimeout);
    if (this.cancellable) this.cancellable.cancel();
  }

  onExitWarning = () => this.setState({ isErrored: false });

  showDetail = (iri) => {
    const link = this.buildLink(iri, this.state.activeResource);
    const currentPath = this.props.location.pathname + this.props.location.hash;
    if (currentPath !== link) this.props.history.push(link);
  };

  addNewResource = async (resourceIRI, reservationID, label) => {
    const defaultClasses = this.props.data.defaultClass || [];
    const newResource = {
      id: resourceIRI,
      type: ["Resource", ...defaultClasses],
      inDataset: [this.props.id],
      ...(this.props.data.activateWorkflow) && { status: [STATUS_DRAFT] },
      label: { [this.props.defaultLang]: label }
    };
    await GS.insertResource(newResource, resourceIRI, this.props.graph, [], reservationID);
    this.showDetail(resourceIRI);
    return resourceIRI;
  };

  addExistingNode = (iri) => {
    GS.loadData([iri], {}, this.props.graph).then(() => {
      if (GS.isLoaded(iri, this.props.graph)) {
        const data = GS.data(iri, this.props.graph);
        if (!data.inDataset || !data.inDataset.includes(this.props.id)) {
          GS.addProperty(iri, this.props.graph, "inDataset", this.props.id);
          this.showDetail(iri);
        }
      }
      else {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({
          isWarning: true,
          message: "This resource has just been deleted and cannot be added."
        });
      }
    });
  };

  deleteResource = (iri) => {
    GS.deleteResource(iri, this.props.graph);
    const deletedResourcesList = [...this.state.deletedResources];
    deletedResourcesList.push(iri);
    this.setState({
      deletedResources: deletedResourcesList
    });
    this.showDetail(null);
  };

  addToDataset = (conceptIRI) => GS.addProperty(conceptIRI, this.props.graph, "inDataset", this.props.id);

  componentDidUpdate() {
    GS.setStore("dm", this.props.location.pathname + this.props.location.hash);
  }

  setViewLanguage = (lang) => {
    // To avoid API overloading we wait until any outstanding requests have completed before changing language
    const inFlight = GS.isLoadingInFlight();
    if (inFlight) {
      if (this.langTimeout) clearTimeout(this.langTimeout);
      this.langTimeout = setTimeout(() => this.setViewLanguage(lang), 250);
    }
    else {
      this.setState({ viewLang: lang });
    }
  };

  render() {

    const { deletedResources, tabValue, isErrored, activeResource, viewLang, message } = this.state;
    const { id, data, defaultLang, project, graph, entitlements, projectUsers, ontologyProjects } = this.props;
    const { showIRI } = data;

    if (isErrored) return <Warning message={message} onExitWarning={this.onExitWarning} />;

    const iriOptions = {
      showIRI,
      removeSpaces: data.iriRemoveSpaces,
      replaceSpaces: data.iriReplaceSpaces,
      handleIRIclash: true,
      removeSpecialCharacters: data.iriRemoveSpecialCharacters,
      replaceSpecialCharacters: data.iriReplaceSpecialCharacters,
      minimiseEncoding: data.minimiseEncoding
    };

    const isReadOnly = checkResourceEntitlements(data, ENTITLEMENTS.EDIT_DATASET);

    const appBar = <InAppBar
      tabValue={tabValue}
      onChangeTab={this.changeTab}
      tabItems={tabItems} />;

    return (
      <React.Fragment>
        <ComponentTitle title={data.title} id={data.id} viewLang={viewLang} defaultLang={defaultLang} onClick={this.toggleDetail} />
        <Grid container spacing={0}>
          <Grid item xs={12}>
            {tabValue === 0 &&
              <ResourceView
                appBar={appBar}
                entitlements={entitlements}
                graph={graph}
                projectUsers={projectUsers}
                useWorkflow={data.activateWorkflow === true}
                iriOptions={iriOptions}
                isReadOnly={isReadOnly}
                project={project}
                ontologyProjects={ontologyProjects}
                onAddNewNode={this.addNewResource}
                onAddExistingNode={this.addExistingNode}
                addToDataset={this.addToDataset}
                defaultClasses={data.defaultClass}
                activeResource={activeResource}
                defaultLang={defaultLang}
                viewLang={viewLang}
                useSlashIRI={data.useSlashIRI || false}
                useUuidIRI={data.useUuidIRI || false}
                useCounterIRI={data.useCounterIRI || false}
                counters={data.useCounterIRI ?
                  {
                    primaryCounter: data.primaryCounter
                  }
                  :
                  {}
                }
                onSelect={this.showDetail}
                onSelectViewLanguage={this.setViewLanguage}
                deletedResources={deletedResources}
                onDeleteResource={this.deleteResource}
                onFoundResource={this.setFoundResource}
                data={data}
                datasetIRI={id}
              />
            }
          </Grid>
        </Grid>
      </React.Fragment>
    );
  }

}

DatasetEditorContainer.propTypes = {
  id: PropTypes.string.isRequired,
  history: PropTypes.object,
  location: PropTypes.object,
  entitlements: PropTypes.array.isRequired,
  datasets: PropTypes.array,
  projectUsers: PropTypes.array,
  ontologyProjects: PropTypes.array,
  toggleDetail: PropTypes.func,
  graph: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  project: PropTypes.string.isRequired,
  defaultLang: PropTypes.string.isRequired
};

export default withRouter(withGS(DatasetEditorContainer, ["ALL"], null, {}));